import { AssetHeadlineFigure } from "@hydrologiq/gql-experience/graphql"
import PaginatedFigureCard from "@hydrologiq/shared/figures/generic/PaginatedFigureCard"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import TrendIndicator from "@hydrologiq/shared/figures/generic/TrendIndicator"

interface FuelSavedItem {
  value: string
}

interface FuelSavedFigureProps {
  data?: AssetHeadlineFigure
  loading?: boolean
  error?: string
  isPlaceholder?: boolean
  compact?: boolean
}

/**
 * Hybrid "Savings" card — carousel toggling between:
 *   1. Fuel saved (litres)
 *   2. CO2e saved (kg)
 *
 * Both pages share the same trend (yesterday vs day before), shown next to
 * the "yesterday" subtext. Uses PaginatedFigureCard for the flip UX.
 */
function FuelSavedFigure({
  data,
  loading,
  error,
  isPlaceholder,
  compact = true,
}: FuelSavedFigureProps) {
  const fuelSavedLitres = data?.value ?? 0
  const co2eSavedKg = (data?.metadata?.co2e_saved_kg as number) ?? 0
  const trendPct = data?.metadata?.trend_pct as number | undefined

  const items: FuelSavedItem[] = data?.available
    ? [
        { value: `${fuelSavedLitres.toFixed(1)} L` },
        { value: `${co2eSavedKg.toFixed(1)} kgCO2e` },
      ]
    : []

  // More savings is good — increase is green, decrease is red
  const trend =
    trendPct != null && trendPct !== 0 ? (
      <TrendIndicator
        change={trendPct}
        goodDirection="increase"
        tooltipText="Compared to the day before"
      />
    ) : undefined

  return (
    <PaginatedFigureCard
      heading="Savings"
      loading={loading}
      compact={compact}
      items={items}
      emptyState={
        <FigureContent.Root compact={compact}>
          <FigureContent.SubText compact={compact}>yesterday</FigureContent.SubText>
          <FigureContent.Value compact={compact}>—</FigureContent.Value>
        </FigureContent.Root>
      }
      renderItem={(item) => (
        <FigureContent.Root compact={compact}>
          <FigureContent.SubText link={trend} compact={compact}>
            yesterday
          </FigureContent.SubText>
          <FigureContent.Value compact={compact}>{item.value}</FigureContent.Value>
        </FigureContent.Root>
      )}
    />
  )
}

export default FuelSavedFigure
